/**
 * Renderer-side brand utilities.
 * Brand is inferred from window.location.hostname (matches VITE_APP_URL per build).
 */

export type Brand = 'dreambooth' | 'dreambooth-staging' | 'bahela' | 'klakklikkluk'

function getBrandFromHost(): Brand {
  if (typeof window === 'undefined') return 'dreambooth'
  const host = window.location.hostname
  if (host.includes('bahela')) return 'bahela'
  if (host.includes('klakklikkluk')) return 'klakklikkluk'
  if (host.includes('staging')) return 'dreambooth-staging'
  return 'dreambooth'
}

let cachedBrand: Brand | null = null

export function getBrand(): Brand {
  if (cachedBrand === null) cachedBrand = getBrandFromHost()
  return cachedBrand
}

export function isBahela(): boolean {
  return getBrand() === 'bahela'
}

export function isDreambooth(): boolean {
  const b = getBrand()
  return b === 'dreambooth' || b === 'dreambooth-staging'
}

export function isKlakklikkluk(): boolean {
  return getBrand() === 'klakklikkluk'
}

export function getBrandProductName(): string {
  const brand = getBrand()
  if (brand === 'bahela') return 'Bahela'
  if (brand === 'klakklikkluk') return 'Klakklikkluk'
  if (brand === 'dreambooth-staging') return 'Dreambooth Staging'
  return 'Dreambooth'
}

export function getGalleryBaseUrl(): string {
  return process.env.VITE_GALLERY_URL || 'https://dreamboothstudio.com/gallery'
}

/** Support/contact domain for consent and help text */
export function getSupportDomain(): string {
  const brand = getBrand()
  if (brand === 'bahela') return 'bahela.id'
  if (brand === 'klakklikkluk') return 'klakklikkluk.com'
  return 'dreamboothstudio.com'
}

export function getDocumentTitle(): string {
  return `${getBrandProductName()} - Photobooth App`
}

/**
 * Square, opaque home-screen icon for the current brand — what iOS reads out of
 * `link[rel=apple-touch-icon]` when someone taps Add to Home Screen. App.tsx
 * swaps it into the DOM alongside the favicon, so a booth on app.bahela.id
 * lands on the iPad Home Screen as Bahela and not as Dreambooth.
 *
 * Deliberately NOT getBrandLogoPath(): that is the header lockup — a glyph plus
 * wordmark for Bahela, and .webp for two of the three brands. An apple-touch
 * icon has to be a square PNG with NO transparency, because iOS flattens alpha
 * onto black. See scripts/generatePwaIcons.mjs, which generates these three.
 *
 * dreambooth-staging shares the Dreambooth icon on purpose.
 */
export function getBrandAppleTouchIconPath(): string {
  const brand = getBrand()
  if (brand === 'bahela') return '/icons/apple-touch-icon-bahela.png'
  if (brand === 'klakklikkluk') return '/icons/apple-touch-icon-klakklikkluk.png'
  return '/icons/apple-touch-icon.png'
}

/** Logo image path for navbar/header (public URL path) */
export function getBrandLogoPath(): string {
  const brand = getBrand()
  if (brand === 'bahela') return '/assets/bahela/bahela-green.webp'
  if (brand === 'klakklikkluk') return '/assets/klakklikkluk/icon.png'
  return '/assets/images/dreambooth.png'
}
